import type { LucideIcon } from "lucide-react";
import { cn } from "src/lib/utils";

interface AssessmentChoiceProps {
  label: string;
  description: string;
  icon: LucideIcon;
  selected: boolean;
  disabled?: boolean;
  badge?: string;
  onClick: () => void;
}

export function AssessmentChoice({
  label,
  description,
  icon: Icon,
  selected,
  disabled = false,
  badge,
  onClick,
}: AssessmentChoiceProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-3.5 rounded-xl border px-3.5 py-3.5 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        disabled && "cursor-not-allowed opacity-60",
        selected
          ? "border-rfpurple-600 bg-rfpurple-50"
          : "border-rfpurple-200 bg-white hover:border-rfpurple-300 hover:bg-rfpurple-50/60"
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#f0e9ff] text-rfpurple-700">
        <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="font-roboto text-[15px] font-semibold text-[#3e1168]">{label}</span>
          {badge && (
            <span className="text-xs font-medium text-rfpurple-700">{badge}</span>
          )}
        </span>
        <span className="mt-0.5 block text-sm leading-snug text-gray-600">{description}</span>
      </span>
      <span
        className={cn(
          "h-2 w-2 shrink-0 rounded-full",
          selected ? "bg-[#ff7f52]" : "bg-rfpurple-200"
        )}
        aria-hidden="true"
      />
    </button>
  );
}
